import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  canRedo,
  canUndo,
  initHistory,
  push,
  redo as redoOp,
  resetHistory,
  undo as undoOp,
  type History,
} from './undo-redo'

export interface UseHistoryResult<T> {
  state: T
  set: (next: T) => void
  update: (updater: (current: T) => T) => void
  reset: (next: T) => void
  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
}

export function useHistory<T>(initial: T, limit?: number): UseHistoryResult<T> {
  const [history, setHistory] = useState<History<T>>(() => initHistory(initial, limit))

  const set = useCallback((next: T) => {
    setHistory(prev => push(prev, next))
  }, [])

  const update = useCallback((updater: (current: T) => T) => {
    setHistory(prev => push(prev, updater(prev.present)))
  }, [])

  const reset = useCallback((next: T) => {
    setHistory(prev => resetHistory(prev, next))
  }, [])

  const undo = useCallback(() => {
    setHistory(prev => undoOp(prev))
  }, [])

  const redo = useCallback(() => {
    setHistory(prev => redoOp(prev))
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      if (!meta) return
      // Let the browser handle undo/redo natively while focus is inside a
      // text-editing element (input, textarea, select, or contenteditable).
      const target = e.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
        // isContentEditable reflects the computed state; also guard via the raw
        // attribute for jsdom compatibility in test environments.
        if (target.isContentEditable || target.getAttribute('contenteditable') === 'true') return
      }
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault()
        if (e.shiftKey) redo()
        else undo()
      } else if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault()
        redo()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undo, redo])

  return useMemo(
    () => ({
      state: history.present,
      set,
      update,
      reset,
      undo,
      redo,
      canUndo: canUndo(history),
      canRedo: canRedo(history),
    }),
    [history, set, update, reset, undo, redo]
  )
}
